// src/pages/Wallet/utils/gasUtils.js
import { parseUnits } from 'ethers';
/* global BigInt */
// Define a minimum gas price to prevent zero gas issues
const MIN_GAS_PRICE_GWEI = '0.000001'; // Minimum gas price (1 wei = 0.000000000000001 gwei)

/**
 * Format gas price for display according to requirements:
 * Show 3 decimal places after all leading zeros
 * 
 * @param {string|number} gweiValue - Gas price in gwei
 * @returns {string} - Formatted gas price for display
 */
export const formatGasPriceForDisplay = (gweiValue) => {
  if (!gweiValue || isNaN(parseFloat(gweiValue))) return '0.00';
  
  const value = parseFloat(gweiValue);
  if (value === 0) return '0.00';
  
  // Convert to string to find leading zeros after decimal
  const strValue = value.toString();
  const decimalIndex = strValue.indexOf('.');
  
  // If no decimal or value >= 0.1, show 3 decimal places
  if (decimalIndex === -1 || value >= 0.1) {
    return value.toFixed(9);
  }
  
  // For values with leading zeros after decimal
  let leadingZeros = 0;
  let index = decimalIndex + 1;
  
  // Count leading zeros after decimal point
  while (index < strValue.length && strValue[index] === '0') {
    leadingZeros++;
    index++;
  }
  
  // Format with 3 significant digits after the first non-zero digit
  const totalDecimals = leadingZeros + 3;
  return value.toFixed(totalDecimals);
};

/**
 * Format gas price for transaction submission
 * Preserve exact precision for calculations, but format to 3 significant digits
 * 
 * @param {string|number} gweiValue - Gas price in gwei
 * @returns {string} - Formatted gas price for transaction
 */
export const formatGasPriceForTransaction = (gweiValue) => {
  // Same implementation as formatGasPriceForDisplay for consistency
  return formatGasPriceForDisplay(gweiValue);
};

/**
 * Calculate gas price based on gas option and dynamic network gas price
 * 
 * @param {string} option - Gas speed option ('slow', 'medium', 'fast', 'custom')
 * @param {string} customGasPrice - User's custom gas price in gwei
 * @param {string} dynamicGasPrice - Current network gas price in gwei
 * @returns {string} - Calculated gas price in gwei as a string
 */
export const calculateGasPrice = (option, customGasPrice, dynamicGasPrice) => {
  // Validate inputs and log for debugging
  console.log(`calculateGasPrice called with option=${option}, customGasPrice=${customGasPrice}, dynamicGasPrice=${dynamicGasPrice}`);
  
  const currentGasPrice = dynamicGasPrice ? parseFloat(dynamicGasPrice) : 3;
  
  // Calculate the gas price based on the option
  let calculatedPrice;
  switch (option) {
    case 'slow':
      calculatedPrice = (currentGasPrice * 0.8).toString();
      break;
    case 'medium':
      calculatedPrice = currentGasPrice.toString();
      break;
    case 'fast':
      calculatedPrice = (currentGasPrice * 1.3).toString();
      break;
    case 'custom':
      calculatedPrice = customGasPrice || "3";
      break;
    default:
      calculatedPrice = currentGasPrice.toString();
  }
  
  // Ensure the gas price is not below the minimum
  if (parseFloat(calculatedPrice) < parseFloat(MIN_GAS_PRICE_GWEI)) {
    calculatedPrice = MIN_GAS_PRICE_GWEI;
  }
  
  console.log(`Calculated gas price: ${calculatedPrice} gwei (raw)`);
  
  // Return the raw calculated price for internal use
  return calculatedPrice;
};

/**
 * Get gas limit for different transaction types
 * 
 * @param {string} transactionType - Type of transaction
 * @returns {number} - Gas limit in gas units
 */
export const getGasLimit = (transactionType) => {
  switch (transactionType) {
    case 'nativeTransfer':
      return 30000;  // Standard ETH transfer
    case 'erc20Transfer':
      return 100000;  // ERC-20 token transfer
    case 'swap':
      return 350000; // Token swap (higher to account for complexity)
    case 'approve':
      return 60000;  // ERC-20 approval
    default:
      return 10000;  // Default fallback
  }
};

/**
 * Estimate gas fee in native token units
 * 
 * @param {string} gasPriceGwei - Gas price in gwei
 * @param {number} gasLimit - Gas limit in gas units
 * @returns {string} - Estimated gas fee in ether
 */
export const estimateGasFee = (gasPriceGwei, gasLimit) => {
  try {
    // Ensure gas price is not zero or negative
    const validGasPriceGwei = parseFloat(gasPriceGwei) <= 0 ? MIN_GAS_PRICE_GWEI : gasPriceGwei;
    
    // Parse gas price to BigInt
    const gasPriceBN = parseUnits(validGasPriceGwei, 'gwei');
    
    // Calculate total gas fee
    const gasFeeWei = gasPriceBN * BigInt(gasLimit);
    
    // Convert to ether (divide by 10^18)
    const gasFeeEther = Number(gasFeeWei) / 1e18;
    
    return gasFeeEther.toFixed(9);
  } catch (err) {
    console.error("Error estimating gas fee:", err);
    return "0.00000001"; // Return a small non-zero value as fallback
  }
};

/**
 * Format gas settings for display
 * 
 * @param {string} option - Gas speed option
 * @param {string} gasPrice - Gas price in gwei
 * @returns {Object} - Formatted gas settings for display
 */
export const formatGasSettings = (option, gasPrice) => {
  const speedLabel = option.charAt(0).toUpperCase() + option.slice(1);
  return {
    price: formatGasPriceForDisplay(gasPrice),
    speed: speedLabel
  };
};